/**
 * What the statistics conclude — each finding a sentence, the figures it rests
 * on, and, where there is something to do about it, the change it suggests and
 * the verb that leads there.
 *
 * The pages lead with these, in two groups: **to act on** — what costs more
 * than it needs to, or fails — sorted by what is at stake in the period, and
 * **worth knowing** — how the work went, against the period before. A chart is
 * drawn only beside a finding, and only the one that supports its sentence
 * (`chart`): a period's counters are not an answer to anything until something
 * has compared them — with the period before, with the median, with what the
 * same tokens cost on the tier the work needed.
 *
 * Every threshold here is deliberately plain (a 2× median, three samples before
 * a rate is quoted) so that a finding can be checked by hand from its evidence.
 * The period's own headline figures — what was spent, what landed, what a
 * landing cost — are not findings: they are the key figures above them
 * (`figures.ts`).
 */
import {
  cacheHitRatio,
  costPerLanded,
  formatDay,
  formatDuration,
  formatPercent,
  formatTokens,
  formatUsd,
  kindLabel,
  pairLabel,
  successRate,
  tokensOf,
  type Analysis,
  type PairRow
} from './analysis';
import type { StepKind } from './day';
import { costMicros, isLightModel, lightModelFor, priceOf } from './prices';

/** What a finding is about, as the page's mono label names it. */
export type FindingSubject =
  | 'COST'
  | 'EFFORT'
  | 'QUALITY'
  | 'SUCCESS'
  | 'LANDED'
  | 'STEPS'
  | 'CONFLICTS'
  | 'TIME'
  | 'CACHE'
  | 'LOAD';

/** `act`: costs more than it needs to, or fails. `know`: how the work went. */
export type FindingGroup = 'act' | 'know';

/**
 * Where a finding's verb leads: the workflows (a step's tier), an agent's own
 * settings (its default effort), or the row of the figures underneath that the
 * sentence names — a model and effort, or a day (`target`).
 */
export interface FindingVerb {
  go: 'workflows' | 'agent' | 'pair' | 'day';
  label: string;
  /** The whole sentence, for the tooltip — the label is three words at most. */
  tip: string;
  /** With `go: 'agent'`: whose settings. */
  agent?: 'claude-code' | 'codex';
}

/**
 * The one chart beside a finding, as values — the page draws it
 * (`sparkGeometry` in `figures.ts` turns a series into points).
 *
 *   series   one value per day of the period, null where the day has none;
 *            `ref` a dashed level (a median, the period before), `mark` the
 *            index of the day the sentence names, `zero` to keep 0 on the scale
 *   compare  two amounts side by side, the first drawn whole: `ratio` is b over a
 *   strip    one dot per model · effort at `x` (0–100) around the median's `mid`
 *   meters   a few labelled shares, `ratio` 0–1
 */
export type FindingChart =
  | { kind: 'series'; values: (number | null)[]; ref?: number; mark?: number; zero?: boolean; note: string }
  | { kind: 'compare'; a: string; av: string; b: string; bv: string; ratio: number }
  | { kind: 'strip'; dots: { x: number; on: boolean }[]; mid: number; lo: string; hi: string; note: string }
  | { kind: 'meters'; rows: { label: string; value: string; ratio: number }[] };

export interface Finding {
  id: string;
  group: FindingGroup;
  subject: FindingSubject;
  /** One sentence, the conclusion. */
  text: string;
  /** The figures it was drawn from. */
  evidence: string;
  /** What to change, when there is something. */
  suggestion?: string;
  verb?: FindingVerb;
  /** The pair label (`opus · xhigh`) or the day (`2026-10-03`) the sentence names. */
  target?: string;
  /** Micro-dollars this finding puts at stake in the period — what "to act on" is sorted by. */
  stake: number;
  chart?: FindingChart;
}

/** How many of something before a rate over it is worth a sentence. */
export const MIN_SAMPLE = 3;
/** Below this a stake is not worth printing: "≈ $0.12 at stake" is noise. */
const STAKE_FLOOR = 500_000;

export function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** `down 23% from the previous period`, or null when there is nothing to compare with. */
export function change(
  now: number | null,
  before: number | null,
  unit = 'the previous period'
): { ratio: number; words: string } | null {
  if (now === null || before === null || before === 0) return null;
  const ratio = now / before - 1;
  if (Math.abs(ratio) < 0.05) return { ratio, words: `about the same as ${unit}` };
  return { ratio, words: `${ratio < 0 ? 'down' : 'up'} ${formatPercent(Math.abs(ratio))} from ${unit}` };
}

/** `≈ $4.20`, or null under the floor. */
export function stakeWords(finding: Pick<Finding, 'stake'>): string | null {
  return finding.stake >= STAKE_FLOOR ? `≈ ${formatUsd(finding.stake)}` : null;
}

/** Two amounts side by side, the first drawn whole and the second against it. */
function compare(a: string, av: number, b: string, bv: number, fmt: (v: number) => string, floor = 0): FindingChart {
  const ratio = av > 0 ? Math.max(floor, Math.min(1, bv / av)) : 0;
  return { kind: 'compare', a, av: fmt(av), b, bv: fmt(bv), ratio };
}

const agentOf = (model: string): 'claude-code' | 'codex' => (model.startsWith('gpt') ? 'codex' : 'claude-code');

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** Which page a verb's tooltip is written for: the app's settings, or the organization's. */
export type FindingsWhere = 'app' | 'account';

export interface FindingsInput {
  current: Analysis;
  previous: Analysis;
  /** `the previous 30 days`, as the sentence names the comparison. */
  previousLabel: string;
  where?: FindingsWhere;
}

const SHOW_PAIR = { go: 'pair', label: 'Show in table', tip: 'Shows its row in By model · effort, below.' } as const;
const SHOW_DAY = { go: 'day', label: 'Show the day', tip: 'Shows the day in By day, below.' } as const;
const cplOf = (x: { cost: number; landed: number }): number | null => (x.landed ? x.cost / x.landed : null);
const rateOf = (x: { started: number; landed: number }): number | null => (x.started ? x.landed / x.started : null);

/** Every finding of the period: to act on first, by stake, then worth knowing in reading order. */
export function findings({ current, previous, previousLabel, where = 'app' }: FindingsInput): Finding[] {
  const act: Finding[] = [];
  const know: Finding[] = [];
  const t = current.totals;
  const p = previous.totals;
  const days = current.daily;
  if (t.turns === 0 && t.started === 0) return [];

  // Money spent with nothing to show for it.
  if (t.landed === 0 && t.cost > 0) {
    act.push({
      id: 'none-landed',
      group: 'act',
      subject: 'COST',
      text: `No task landed in this period, and the agents spent ${formatUsd(t.cost)}.`,
      evidence: `${t.turns} turns, ${plural(t.started, 'task')} started.`,
      stake: 0,
      chart: { kind: 'series', values: days.map((d) => d.cost), note: 'spend, a day each' }
    });
  }

  // Mechanical work done on a model sized for judgement.
  const heavy = current.buckets.filter((b) => b.kind === 'mech' && !isLightModel(b.model) && (b.bucket.c ?? 0) > 0);
  if (heavy.length) {
    const spent = heavy.reduce((sum, b) => sum + (b.bucket.c ?? 0), 0);
    const light = heavy.reduce(
      (sum, b) =>
        sum +
        costMicros(lightModelFor(b.model), {
          input: b.bucket.i ?? 0,
          output: b.bucket.o ?? 0,
          cacheRead: b.bucket.cr ?? 0,
          cacheWrite: b.bucket.cw ?? 0
        }),
      0
    );
    const turns = heavy.reduce((sum, b) => sum + (b.bucket.t ?? 0), 0);
    if (spent - light >= Math.max(1_000_000, spent * 0.3)) {
      const top = [...heavy].sort((a, b) => (b.bucket.c ?? 0) - (a.bucket.c ?? 0))[0];
      act.push({
        id: 'mechanical-tier',
        group: 'act',
        subject: 'COST',
        text: `Mechanical steps ran ${plural(turns, 'turn')} on larger models (mostly ${pairLabel(top.model, top.effort)}) and cost ${formatUsd(spent)}; at the light tier the same tokens would have cost about ${formatUsd(light)}.`,
        evidence: `Same token counts, priced on ${[...new Set(heavy.map((b) => lightModelFor(b.model)))].join(' / ')} at list price.`,
        suggestion: 'Give the test, check and commit steps of your workflows the light tier (Haiku, or GPT-5.6 Luna).',
        verb: {
          go: 'workflows',
          label: 'Open Workflows',
          tip:
            where === 'account'
              ? 'The organization’s workflows — each step names its model and effort.'
              : 'Workflows — each step names the model and effort it runs on.'
        },
        stake: spent - light,
        chart: compare('spent', spent, 'light tier', light, formatUsd, 0.03)
      });
    }
  }

  // A model · effort whose landings cost more than twice the median.
  const withLanded = current.pairs.filter((row) => row.landed > 0);
  if (withLanded.length >= MIN_SAMPLE) {
    const cpls = withLanded.map((row) => row.cost / row.landed);
    const mid = median(cpls);
    const hi = Math.max(...cpls);
    for (const row of withLanded.filter((one) => one.cost / one.landed > 2 * mid).slice(0, 2)) {
      const cpl = row.cost / row.landed;
      const label = pairLabel(row.model, row.effort);
      act.push({
        id: `outlier-pair-${row.model}-${row.effort}`,
        group: 'act',
        subject: 'COST',
        text: `Work on ${label} cost ${formatUsd(cpl)} per landed task, ${(cpl / mid).toFixed(1)}× the median across models.`,
        evidence: `${formatUsd(row.cost)} for ${plural(row.landed, 'landed task')}; median ${formatUsd(mid)}.`,
        verb: { ...SHOW_PAIR },
        target: label,
        stake: Math.round(row.cost - row.landed * mid),
        chart: {
          kind: 'strip',
          dots: withLanded.map((one) => ({ x: Math.round((one.cost / one.landed / hi) * 100), on: one === row })),
          mid: Math.round((mid / hi) * 100),
          lo: '$0',
          hi: formatUsd(hi),
          note: 'per landed task, one dot per model · effort'
        }
      });
    }
  }

  const effort = effortFinding(current.pairs, where);
  if (effort) act.push(effort);

  // The days a landing cost more than twice the period's median.
  const landedDays = days.filter((d) => d.landed > 0);
  if (landedDays.length >= MIN_SAMPLE) {
    const mid = median(landedDays.map((d) => d.cost / d.landed));
    const high = landedDays.filter((d) => d.cost / d.landed > 2 * mid).sort((a, b) => b.cost / b.landed - a.cost / a.landed);
    if (high.length) {
      const worst = high[0];
      act.push({
        id: 'outlier-days',
        group: 'act',
        subject: 'COST',
        text: `On ${high
          .slice(0, 3)
          .map((d) => formatDay(d.day))
          .join(', ')} a landed task cost more than twice the period's median.`,
        evidence: `${high
          .slice(0, 3)
          .map((d) => `${formatUsd(d.cost / d.landed)} (${(d.cost / d.landed / mid).toFixed(1)}×)`)
          .join(', ')} against a median of ${formatUsd(mid)}.`,
        verb: { ...SHOW_DAY },
        target: worst.day,
        stake: Math.round(worst.cost - worst.landed * mid),
        chart: {
          kind: 'series',
          values: days.map(cplOf),
          ref: mid,
          mark: days.indexOf(worst),
          note: 'per landed task, a day each · median dashed'
        }
      });
    }
  }

  // Re-asking as a quality signal: rising, it is something to act on.
  if (t.started >= MIN_SAMPLE && t.reruns > 0) {
    const per = t.reruns / t.started;
    const before = p.started > 0 ? p.reruns / p.started : null;
    const trend = change(per, before, previousLabel);
    const rising = !!trend && trend.ratio > 0.25 && t.reruns >= MIN_SAMPLE;
    (rising ? act : know).push({
      id: 'reruns',
      group: rising ? 'act' : 'know',
      subject: 'QUALITY',
      text: `Work was asked again or reopened ${per.toFixed(1)} times per task started${trend ? `, ${trend.words}` : ''}.`,
      evidence: `${plural(t.reruns, 'follow-up')} or reopens across ${t.started} tasks.`,
      ...(per >= 1 ? { suggestion: 'Tasks that need asking again often are worth a sharper first prompt or a planning step.' } : {}),
      stake: 0,
      chart: {
        kind: 'series',
        values: days.map((d) => (d.started ? d.reruns / d.started : null)),
        ...(before !== null ? { ref: before } : {}),
        zero: true,
        note: before !== null ? `a day each · ${previousLabel} dashed` : 'a day each'
      }
    });
  }

  // How tasks started on each pair fared.
  const rated = current.pairs.filter((row) => row.started >= MIN_SAMPLE);
  if (rated.length >= 2) {
    const sorted = [...rated].sort((a, b) => (rateOf(b) ?? 0) - (rateOf(a) ?? 0));
    const best = sorted[0];
    const worst = sorted[sorted.length - 1];
    if ((rateOf(best) ?? 0) - (rateOf(worst) ?? 0) >= 0.15) {
      know.push({
        id: 'success-by-pair',
        group: 'know',
        subject: 'SUCCESS',
        text: `Tasks started on ${pairLabel(best.model, best.effort)} landed ${formatPercent(rateOf(best) ?? 0)} of the time; on ${pairLabel(worst.model, worst.effort)}, ${formatPercent(rateOf(worst) ?? 0)}.`,
        evidence: `${best.landed}/${best.started} against ${worst.landed}/${worst.started}.`,
        verb: { ...SHOW_PAIR },
        target: pairLabel(worst.model, worst.effort),
        stake: 0,
        chart: {
          kind: 'strip',
          dots: rated.map((row) => ({ x: Math.round((rateOf(row) ?? 0) * 100), on: row === best || row === worst })),
          mid: Math.round(median(rated.map((row) => rateOf(row) ?? 0)) * 100),
          lo: '0%',
          hi: '100%',
          note: 'landed of started, one dot per model · effort'
        }
      });
    }
  }

  // The kinds of step, and how often each passes.
  const kinds = current.kinds.filter((row) => row.kind !== 'task' && row.stepsPassed + row.stepsFailed >= MIN_SAMPLE);
  if (kinds.length) {
    const name = (kind: StepKind): string => kindLabel(kind).label.toLowerCase();
    know.push({
      id: 'steps',
      group: 'know',
      subject: 'STEPS',
      text: `Workflow steps: ${kinds.map((row) => `${name(row.kind)} passed ${row.stepsPassed} of ${row.stepsPassed + row.stepsFailed}`).join('; ')}.`,
      evidence: `${plural(kinds.reduce((sum, row) => sum + row.stepReruns, 0), 'step')} had to run again.`,
      stake: 0,
      chart: {
        kind: 'meters',
        rows: kinds.map((row) => ({
          label: name(row.kind).split(' ')[0],
          value: `${row.stepsPassed}/${row.stepsPassed + row.stepsFailed}`,
          ratio: row.stepsPassed / (row.stepsPassed + row.stepsFailed)
        }))
      }
    });
  }

  // Conflicts: one for fewer than every three landings is something to act on. No chart says more than the sentence.
  if (t.conflicts > 0) {
    const often = t.landed > 0 && t.conflicts / t.landed > 0.3;
    (often ? act : know).push({
      id: 'conflicts',
      group: often ? 'act' : 'know',
      subject: 'CONFLICTS',
      text: `Syncing met conflicts ${plural(t.conflicts, 'time')}${t.landed ? ` — one for every ${(t.landed / t.conflicts).toFixed(1)} landed tasks` : ''}.`,
      evidence: `${plural(t.conflicts, 'conflicted sync')}, ${t.landed} landed.`,
      ...(often ? { suggestion: 'Shorter-lived sessions, or syncing before long runs, meet fewer conflicts.' } : {}),
      stake: 0
    });
  }

  // How long work takes to land.
  if (t.landCount > 0) {
    const avg = t.landMs / t.landCount;
    const prev = p.landCount ? p.landMs / p.landCount : null;
    const trend = change(avg, prev, previousLabel);
    know.push({
      id: 'time-to-land',
      group: 'know',
      subject: 'TIME',
      text: `A task took ${formatDuration(avg)} on average from its session starting to landing${trend ? `, ${trend.words}` : ''}.`,
      evidence: `${plural(t.landCount, 'landing')} measured.`,
      stake: 0,
      chart: {
        kind: 'series',
        values: days.map((d) => (d.landCount ? d.landMs / d.landCount : null)),
        ...(prev !== null ? { ref: prev } : {}),
        note: prev !== null ? `a day each · ${previousLabel} dashed` : 'a day each'
      }
    });
  }

  // The cache.
  const hit = cacheHitRatio(t);
  if (hit !== null && tokensOf(t) > 100_000) {
    const saved = current.buckets.reduce((sum, b) => {
      const price = priceOf(b.model);
      return sum + (b.bucket.cr ?? 0) * Math.max(0, price.input - price.cacheRead);
    }, 0);
    know.push({
      id: 'cache',
      group: 'know',
      subject: 'CACHE',
      text: `${formatPercent(hit)} of the input the models read came from the prompt cache, which saved about ${formatUsd(saved)} at list price.`,
      evidence: `${formatTokens(t.cacheRead)} tokens read from the cache, ${formatTokens(t.cacheWrite)} written to it, ${formatTokens(t.input)} read uncached.`,
      ...(hit < 0.2
        ? { suggestion: 'Fewer, longer conversations reuse their cache; many short ones each pay to write it again.' }
        : {}),
      stake: 0,
      chart: compare('without cache', t.cost + saved, 'paid', t.cost, formatUsd)
    });
  }

  // The busiest day.
  if (days.filter((d) => d.cost > 0).length >= 2) {
    const busiest = [...days].sort((a, b) => b.cost - a.cost)[0];
    know.push({
      id: 'busiest-day',
      group: 'know',
      subject: 'LOAD',
      text: `The busiest day was ${formatDay(busiest.day)}: ${formatUsd(busiest.cost)}, ${busiest.turns} turns, ${busiest.landed} landed.`,
      evidence: `${formatPercent(busiest.cost / Math.max(1, t.cost))} of the period's spend.`,
      verb: { ...SHOW_DAY },
      target: busiest.day,
      stake: 0,
      chart: { kind: 'series', values: days.map((d) => d.cost), mark: days.indexOf(busiest), note: 'spend, a day each' }
    });
  }

  return sortFindings([...act, ...know]);
}

/** To act on by stake, largest first; worth knowing in the order written. */
export function sortFindings(list: Finding[]): Finding[] {
  const act = list.filter((f) => f.group === 'act').sort((a, b) => b.stake - a.stake);
  return [...act, ...list.filter((f) => f.group === 'know')];
}

/** For one model, the lowest effort that lands as often as a higher one for clearly less per landing. */
function effortFinding(pairs: PairRow[], where: FindingsWhere): Finding | null {
  const byModel = new Map<string, PairRow[]>();
  for (const row of pairs) {
    if (!row.effort || row.started < MIN_SAMPLE || !row.landed) continue;
    byModel.set(row.model, [...(byModel.get(row.model) ?? []), row]);
  }
  const order = ['low', 'medium', 'high', 'xhigh', 'max'];
  for (const [model, rows] of byModel) {
    const sorted = [...rows].sort((a, b) => order.indexOf(a.effort) - order.indexOf(b.effort));
    for (let i = 0; i < sorted.length - 1; i += 1) {
      const low = sorted[i];
      for (const high of sorted.slice(i + 1)) {
        const rLow = successRate(low) ?? 0;
        const rHigh = successRate(high) ?? 0;
        const cLow = costPerLanded(low) ?? 0;
        const cHigh = costPerLanded(high) ?? 0;
        if (rLow >= rHigh - 0.05 && cHigh > 0 && cLow <= cHigh * 0.75) {
          const agent = agentOf(model);
          const name = agent === 'claude-code' ? 'Claude Code' : 'Codex';
          return {
            id: 'effort',
            group: 'act',
            subject: 'EFFORT',
            text: `On ${model}, ${low.effort} effort landed as often as ${high.effort} (${formatPercent(rLow)} against ${formatPercent(rHigh)}) for ${formatPercent(1 - cLow / cHigh)} less per landed task.`,
            evidence: `${formatUsd(cLow)} against ${formatUsd(cHigh)} per landed task; ${low.started} and ${high.started} tasks started.`,
            suggestion: `Consider ${low.effort} as the default effort for ${model}.`,
            verb: {
              go: 'agent',
              agent,
              label: `Open ${name}`,
              tip:
                where === 'account'
                  ? `Agents › ${name} — the default effort every member is handed.`
                  : `App settings › ${name} — where its default effort is set.`
            },
            target: pairLabel(high.model, high.effort),
            stake: Math.round((cHigh - cLow) * high.landed),
            chart: compare(high.effort, cHigh, low.effort, cLow, formatUsd)
          };
        }
      }
    }
  }
  return null;
}
